// modify-encounter-creature — client-side reconstruction of
// options/tools/encounter_creatures/forms/form_modify_encounter_creature_inc.php, painted from the
// full-nested cached creature node (casterCache / encounterCache) so opening the GM "Modify Creature"
// side form needs NO server call at all (the queryData() DB-form render is retired). The two dynamic
// <select>s (team / summon_type) are LAZY (data-preload-full="false"): their catalog loads from
// get_items_for_dynamic_select.php only when the user focuses the dropdown — so the OPEN is fully
// client-side. Template details are SELECTed when a template is present; mutations use the existing save:
// field change → modifyEncounterCreature(event) (reads the inputs by id, creature-actions bundle),
// hide/reveal + privacy → queryData(), media manager → a normal link.
//
// FIDELITY: every element id that modifyEncounterCreature() (creature-actions.ts) reads on save, and
// that initializeTomSelect()/initializeQuillEditors() hydrate, is reproduced 1:1 from the PHP render_*
// helpers. The controller <select> is scoped to creatures IN THIS ENCOUNTER (encounterArr
// .encounter_creatures_arr) — the same scoping the PHP form was changed to (2026-06-21). This mirrors
// the inventory-edit-form bundle (the inventory PHP form is likewise retired).

import {
    encounterCreatureField, itemsCol, posCoord, computedValue, layerOf,
    pictureListOf, itemVideos, catalogShell, hasPlayerPermission,
} from '../shared/array-types';
import { buildCarousel } from '../shared/build-carousel';
import { cardEditFetch, cardEditQuery } from '../shared/card-edit-publication';
import { getSwarmTemplateForCreature, SWARM_TEMPLATES } from '../shared/swarm-templates';
import { monsterTemplateSelectionInputs, selectedTemplateDetails } from '../shared/swarm-template-selection';
import { withAsyncDeadline } from '../shared/async-deadline';

export interface EncounterCreatureEditCtx {
    encounter_id?: string;
}

type CreatureRecord = Record<string, unknown>;
function recordOf(value: unknown): CreatureRecord | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value) ? value as CreatureRecord : null;
}

interface ModifyEncounterHost {
    FateTide?: { casterCache?: Map<string, unknown>; encounterCache?: Map<string, unknown> };
    ftItemNameById?: (id: string) => string;
    ftBindVitalitySliderToVMax?: (maxId: string, currentId: string, labelId: string) => void;
    ftSeedCasterFromEncounterCache?: (id: string) => boolean;
    fetchAndCacheCaster?: (id: string) => Promise<unknown>;
    getEncounterArr?: (id: string) => Promise<unknown>;
    renderEditEncounterCreatureForm?: typeof renderEditEncounterCreatureForm;
    renderEditLairForm?: typeof renderEditLairForm;
    buildEncounterLairsReport?: () => unknown | Promise<unknown>;
    Quill?: { find(element: Element): { root: HTMLElement } | null };
}

function modifyEncounterHost(): Window & ModifyEncounterHost {
    return window as unknown as Window & ModifyEncounterHost;
}

/** HTML-escape for attribute values and text content. */
function esc(v: unknown): string {
    return String(v ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
}

/** PC-vs-creature/lair identity for a cached creature blob. `creatureArr` here comes from casterCache
 *  (findCached), and the PC normalization path (query_player_character_arr_inc.php) STRIPS the root
 *  `items` bucket — item_type then rides on encounter_creatures — so `itemsCol(arr,'item_type')` (which
 *  reads items/flat/status, NOT encounter_creatures) returns '' for a player character and misroutes
 *  (PC media -> encounter_creature manager; a lair -> the full creature form). Read
 *  encounter_creatures.item_type FIRST (empty falls through), then itemsCol for non-encounter shells.
 *  Twin of casterItemType() (player-character-sheet/read-helpers) — kept LOCAL to avoid a cross-bundle
 *  import. [[reference_pc_creature_item_type_discriminator]] */
function creatureArrItemType(arr: unknown): string {
    const ecRaw = recordOf(recordOf(arr)?.encounter_creatures)?.item_type;
    const ec = Array.isArray(ecRaw) ? ecRaw[0] : ecRaw;
    if (ec !== undefined && ec !== null && ec !== '') return String(ec);
    return String(itemsCol(arr, 'item_type') ?? '');
}

/** Trusted iconMap() HTML (or '' on miss) — mirrors PHP icon_ph(). */
function icon(key: string, w?: number, h?: number, bw?: boolean, title?: string): string {
    if (!key) return '';
    try { return iconMap(key, w, h, bw, title); } catch { return ''; }
}

type ModifyEncounterWindow = Record<string, unknown>;

function mecCall(name: string, ...args: unknown[]): unknown {
    const fn = (window as unknown as ModifyEncounterWindow)[name];
    return typeof fn === 'function' ? fn(...args) : undefined;
}

function mecAttrs(action: string, attrs: Record<string, string> = {}): string {
    const pairs = [`data-ft-mec-action="${esc(action)}"`];
    for (const [key, value] of Object.entries(attrs)) {
        // HTML lowercases attribute names, so `data-ft-hideTarget` reads back as dataset.fthidetarget,
        // NOT dataset.ftHideTarget. Kebab-case the key so the camelCase dataset readers resolve.
        pairs.push(`data-ft-${key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}="${esc(value)}"`);
    }
    return pairs.join(' ');
}

function mecPayload(raw: string): Record<string, string> {
    const payload: Record<string, string> = {};
    for (const pair of raw.split('|')) {
        const idx = pair.indexOf('=');
        if (idx <= 0) continue;
        payload[pair.slice(0, idx)] = pair.slice(idx + 1);
    }
    return payload;
}

document.addEventListener('change', (event) => {
    const el = event.target instanceof Element
        ? event.target.closest<HTMLElement>('[data-ft-mec-change]')
        : null;
    if (!el) return;
    const action = el.dataset.ftMecChange || '';
    if (action === 'modify') mecCall('modifyEncounterCreature', event);
    if (action === 'modify-lair') mecCall('modifyLair', event);
});

document.addEventListener('input', (event) => {
    const el = event.target instanceof HTMLInputElement
        ? event.target.closest<HTMLInputElement>('[data-ft-mec-mirror-target]')
        : null;
    if (!el) return;
    const target = document.getElementById(el.dataset.ftMecMirrorTarget || '') as HTMLInputElement | null;
    if (target) target.value = el.value;
});

document.addEventListener('click', (event) => {
    const el = event.target instanceof Element
        ? event.target.closest<HTMLElement>('[data-ft-mec-action]')
        : null;
    if (!el) return;
    const action = el.dataset.ftMecAction || '';
    if (action === 'visibility') {
        event.preventDefault();
        const bridge = (window as Window & {
            ftTryCommitCreatureVisibility?: (id: string, hidden: boolean) => Promise<'handled' | 'refused'>;
        }).ftTryCommitCreatureVisibility;
        if (!bridge) { mecCall('displayWarning', 'Reload the page to change creature visibility.'); return; }
        void bridge(el.dataset.ftCreatureId || '', el.dataset.ftHidden === '1').then(outcome => {
            if (outcome !== 'handled') return;
            const hide = document.getElementById(el.dataset.ftHideTarget || '');
            const show = document.getElementById(el.dataset.ftShowTarget || '');
            if (hide) hide.style.display = 'none';
            if (show) show.style.display = 'block';
        });
        return;
    }
    if (action === 'query-toggle') {
        event.preventDefault();
        void cardEditQuery(el.dataset.ftUrl || '', 'div_sidelog_warning', mecPayload(el.dataset.ftPayload || '')).then(() => {
            const hide = document.getElementById(el.dataset.ftHideTarget || '');
            const show = document.getElementById(el.dataset.ftShowTarget || '');
            if (hide) hide.style.display = 'none';
            if (show) show.style.display = el.dataset.ftShowDisplay || 'block';
        });
        return;
    }
    if (action === 'select-gender') {
        mecCall('selectBtnGroupValue', el, el.dataset.ftValue || '', 'gender');
        mecCall('modifyEncounterCreature', event);
        return;
    }
    if (action === 'randomize-name') {
        event.preventDefault();
        mecCall('randomizeEncounterCreatureName', event);
    }
});

/** Title-cased label from a snake_case id, mirroring ucwords_and_space(). */
function labelize(name: string): string {
    return name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** String value of an `encounter_creatures`-bucket column (descalared). */
function ecf(ec: unknown, key: string): string {
    const v = encounterCreatureField(ec, key);
    return v === undefined || v === null ? '' : String(v);
}

/** A DOM-id token safe for the carousel container id. */
function domToken(id: string): string {
    return String(id).replace(/[^A-Za-z0-9_]/g, '');
}

// ── individual field builders (each mirrors the matching PHP render_* helper) ──────────────

// render_number_field(...) — labelled number input, onchange modifyEncounterCreature(event).
// `iconTitle` overrides the icon's hover tooltip (else iconMap derives it from the key).
function numberField(id: string, value: string, iconKey: string, min: number, max: number, step: number, iconTitle?: string): string {
    return `
    <div class="d-flex align-items-center gap-2">
        <span class="flex-shrink-0 d-inline-flex align-items-center">${icon(iconKey, 25, 25, true, iconTitle)}</span>
        <input id="${esc(id)}" name="${esc(id)}" class="form-control form-control-sm flex-grow-1" type="number"
            min="${esc(min)}" max="${esc(max)}" step="${esc(step)}" value="${esc(value)}" style="min-width:0;"
            data-ft-mec-change="modify">
    </div>`;
}

// render_checkbox(...) — labelled checkbox.
function checkbox(id: string, iconKey: string, label: string, checked: boolean): string {
    return `
<div class="d-flex align-items-center my-2">
    <div class="me-2">${icon(iconKey)}</div>
    <div class="form-check d-flex align-items-center">
        <input id="${esc(id)}" class="form-check-input me-2" type="checkbox" name="${esc(id)}" value="1"
            ${checked ? 'checked' : ''} data-ft-mec-change="modify">
        <label class="form-check-label" for="${esc(id)}">${esc(label)}</label>
    </div>
</div>`;
}

// render_slider(...) — range + mirrored number input (the number carries the saved id).
function slider(id: string, label: string, iconKey: string, min: number, max: number, step: number, value: number, maxSpanId?: string): string {
    const maxIdAttr = maxSpanId ? ` id="${esc(maxSpanId)}"` : '';
    return `
        <div class='border rounded p-2'>
            <div class='text-center'>${icon(iconKey)} ${esc(label)}</div>
            <div class='d-flex align-items-center'>
                <div class='p-1 flex-shrink-0'><span>${esc(min)}</span></div>
                <div class='p-1 flex-grow-1'>
                    <input id='${esc(id)}Range' class='form-range w-100' type='range' min='${esc(min)}' max='${esc(max)}'
                        step='${esc(step)}' value='${esc(value)}'
                        data-ft-mec-mirror-target="${esc(id)}" data-ft-mec-change="modify">
                </div>
                <div class='p-1 flex-shrink-0'><span${maxIdAttr}>${esc(max)}</span></div>
            </div>
            <div class='d-flex align-items-center justify-content-center'>
                <div>
                    <input id='${esc(id)}' class='form-control' type='number' min='${esc(min)}' max='${esc(max)}'
                        step='${esc(step)}' value='${esc(value)}'
                        data-ft-mec-mirror-target="${esc(id)}Range" data-ft-mec-change="modify">
                </div>
            </div>
        </div>`;
}

// render_textarea(...) — Quill editor container + hidden textarea seeded with the stored value (Quill
// reads data-initial-content on init; the save reads the textarea). The AI prompt/copy buttons are
// cosmetic and not read on save, so omitted on the cache path — same call inventory-edit-form made.
function quillTextarea(base: string, value: string): string {
    const v = esc(value);
    // Icon + label key is the bare layer name (strip the `encounter_creature_` prefix). iconMap()
    // singular-folds (quirks→quirk, motivations→motivation, secrets→secret); the id/data-layer-type-name
    // stay the FULL field name the save handler reads.
    const shortKey = base.replace(/^encounter_creature_/, '');
    return `
    <div class="d-flex flex-column align-items-start justify-content-start mb-4">
        <div class="d-flex align-items-center w-100 mb-2">
            <div class="pe-2">${icon(shortKey)}</div>
            <div class="flex-grow-1"><span class="fw-bold text-decoration-underline">${esc(labelize(shortKey))}:</span></div>
        </div>
        <div class="w-100 quill-editor-container">
            <div class="quill-editor" id="quill_${esc(base)}" data-layer-type-name="${esc(base)}"
                data-initial-content="${v}" data-update-callback="modifyEncounterCreature(event)"></div>
            <textarea id="${esc(base)}" name="${esc(base)}" style="display: none;">${v}</textarea>
        </div>
    </div>`;
}

// render_dynamic_select(...) for an AJAX-backed type (team_type / summon_type): emit the <select> shell
// with the data-attributes the Tom Select loader reads, plus the placeholder + preselected option (whose
// label we resolve from the blob's shelled bucket / catalog, so it shows correctly without any fetch).
// The option pool is LAZY (data-preload-full="false") → Tom Select loads it from
// get_items_for_dynamic_select.php only on focus, so opening the form fires ZERO server calls.
function dynamicSelect(opts: {
    id: string; itemType: string; preselectValue: string; preselectLabel: string; iconKey: string;
    changeAction?: string; // which mecCall the on-change fires: 'modify' (default) or 'modify-lair'
}): string {
    const placeholder = `-- SELECT ${opts.id.replace(/_/g, ' ').toUpperCase()} --`;
    const preselectedJson = opts.preselectValue ? JSON.stringify([opts.preselectValue]) : '[]';
    let options = `<option value="">${esc(placeholder)}</option>`;
    if (opts.preselectValue) {
        options += `<option value="${esc(opts.preselectValue)}" selected>${esc(opts.preselectLabel || opts.preselectValue)}</option>`;
    }
    const changeAction = opts.changeAction || 'modify';
    const selectHtml = `<select class="ts-select dynamic-item-select" id="${esc(opts.id)}" name="${esc(opts.id)}"
        data-item-type="${esc(opts.itemType)}" data-homebrew="false" data-preselected="${esc(preselectedJson)}"
        data-preload-full="false" data-show-icons="true" data-thumbnail-source="icon" data-ft-mec-change="${esc(changeAction)}">${options}</select>`;
    return `<div class="d-flex align-items-center w-100 min-w-0 gap-2">`
        + `<div class="pl-2 flex-shrink-0 d-flex align-items-center">${icon(opts.iconKey, 25, 25, true)}</div>`
        + `<div class="flex-grow-1 min-w-0">${selectHtml}</div></div>`;
}

// The shared visibility bridge saves the browser verdict before flipping the button pair.
function hideButtons(ecId: string): string {
    const e = esc(ecId);
    return `
        <button id='btn_reveal_creature_${e}' type='button' class='btn btn-light border btn-sm' style='display:none;'
            ${mecAttrs('visibility', { creatureId: e, hidden: '0', hideTarget: `btn_reveal_creature_${e}`, showTarget: `btn_hide_creature_${e}` })}>
            ${icon('reveal', 25, 25, true, 'Reveal Creature')}
        </button>
        <button id='btn_hide_creature_${e}' type='button' class='btn btn-dark border btn-sm' style='display:block;'
            ${mecAttrs('visibility', { creatureId: e, hidden: '1', hideTarget: `btn_hide_creature_${e}`, showTarget: `btn_reveal_creature_${e}` })}>
            ${icon('hidden', 25, 25, true, 'Hide Creature')}
        </button>`;
}

// render_item_private_toggle_buttons('encounter_creature', ...) — public/private flip.
function privateButtons(ecId: string, isPrivate: boolean): string {
    const url = `${BASE_URL}options/db_queries/update/update_privacy_inc.php`;
    const e = esc(ecId);
    return `
        <button id='encounter_creature_public_${e}' type='button' class='btn btn-primary btn-sm' style='display:${isPrivate ? 'none' : 'block'};'
            ${mecAttrs('query-toggle', { url, payload: `item_type=encounter_creature|encounter_creature_id=${e}|encounter_creature_private=1`, hideTarget: `encounter_creature_public_${e}`, showTarget: `encounter_creature_private_${e}`, showDisplay: 'block' })}>
            ${icon('public')}
        </button>
        <button id='encounter_creature_private_${e}' type='button' class='btn btn-danger btn-sm' style='display:${isPrivate ? 'block' : 'none'};'
            ${mecAttrs('query-toggle', { url, payload: `item_type=encounter_creature|encounter_creature_id=${e}|encounter_creature_private=0`, hideTarget: `encounter_creature_private_${e}`, showTarget: `encounter_creature_public_${e}`, showDisplay: 'block' })}>
            ${icon('private')}
        </button>`;
}

// Gender button group — hardcoded UUIDs (stable; also in template.js GENDER_UUID_BY_VARIABLE). The
// global selectBtnGroupValue(this, uuid, 'gender') sets #gender + flips the active button.
const GENDER_OPTIONS = [
    '0D629F19-23AC-18BE-B79C-73CB52703F66',
    '1214C099-5021-24B5-5D8D-608B832CE70A',
    '338C4063-2F1E-6A9D-5451-8E6ED3266353',
];
function genderGroup(currentGender: string): string {
    const buttons = GENDER_OPTIONS.map((uuid) => {
        const active = currentGender === uuid ? ' active' : '';
        return `<button type="button" class="btn btn-sm btn-outline-primary rounded m-1${active}"
            ${mecAttrs('select-gender', { value: uuid })} data-value="${esc(uuid)}"
            style="transition: all 0.2s ease;">${icon(uuid)}</button>`;
    }).join('');
    return `
    <div class="p-2 d-flex align-items-center">
        <div class="btn-group btn-group-toggle d-flex align-items-center" role="group" aria-label="Custom select">
            ${buttons}
        </div>
        <input type="hidden" id="gender" value="${esc(currentGender)}" data-ft-mec-change="modify">
    </div>`;
}

/** Resolve a catalog-ref select's display label from the blob (shelled bucket → root catalog →
 *  global name map → the raw UUID), so the preselected option shows a name before the lazy pool loads. */
function selectLabel(creatureArr: unknown, ec: unknown, key: string, id: string): string {
    if (!id) return '';
    const slot = recordOf(recordOf(ec)?.[key]);
    const slotItems = recordOf(slot?.items);
    if (typeof slotItems?.item_name === 'string' && slotItems.item_name !== '') {
        return slotItems.item_name;
    }
    const shell = catalogShell(creatureArr, id);
    if (shell && typeof shell.item_name === 'string' && shell.item_name !== '') return shell.item_name;
    try {
        const host = modifyEncounterHost();
        if (typeof host.ftItemNameById === 'function') {
            const n = host.ftItemNameById(id);
            if (typeof n === 'string' && n !== '') return n;
        }
    } catch (e) { /* */ }
    return id;
}

// Controller <select> options — scoped to the creatures IN THIS ENCOUNTER (encounterArr
// .encounter_creatures_arr), mirroring the PHP form change (2026-06-21). Label = player_name (PCs) →
// encounter_creature_name (monsters): finalize_creature_arr already resolves item_name in that order.
function controllerOptions(encounterArr: unknown, selectedControllerId: string): string {
    let html = `<option value="">-</option>`;
    const encounter = recordOf(encounterArr);
    const roster = Array.isArray(encounter?.encounter_creatures_arr)
        ? encounter.encounter_creatures_arr
        : [];
    let paintedSelected = false;
    for (const value of roster) {
        const participant = recordOf(value);
        const pec = recordOf(participant?.encounter_creatures);
        const pecId = ecf(pec, 'encounter_creature_id');
        if (!pecId) continue;
        // player_name (PCs) → encounter_creature_name (monsters) → item_name. The roster includes
        // player characters, so resolve their display name rather than falling through to "Unknown".
        const player = recordOf(participant?.player_characters);
        const pcName = player
            ? String((Array.isArray(player.player_name)
                ? player.player_name[0]
                : player.player_name) ?? '')
            : '';
        const name = pcName
            || ecf(pec, 'encounter_creature_name')
            || String(itemsCol(participant, 'item_name') ?? '');
        const sel = pecId === selectedControllerId ? ' selected' : '';
        if (sel) paintedSelected = true;
        html += `<option data-tokens="${esc(name)}" value="${esc(pecId)}"${sel}>${esc(name)}</option>`;
    }
    // The creature HAS a controller, but nothing in the roster we can see carries that id — the cached
    // encounter roster lags the controller assignment, or the controller sits outside this encounter.
    // Without an option holding it, the <select> falls back to its first option ("-", value="") and the
    // very next auto-save POSTs an EMPTY controller, silently CLEARING it. That revert needs no stale
    // creature blob at all — only a roster that has not caught up — and it fires on the first save.
    // Paint the truth instead: carry the id in its own selected option (labelled with the raw id, since
    // the roster is exactly what we lack a name from) so an untouched select round-trips unchanged.
    if (selectedControllerId && !paintedSelected) {
        html += `<option data-tokens="${esc(selectedControllerId)}" value="${esc(selectedControllerId)}" selected>${esc(selectedControllerId)}</option>`;
    }
    return html;
}

/**
 * Build the Modify Encounter Creature form into `container` from the cached creature node + the
 * encounter context (for the controller roster). Permission is NOT re-checked here — the save endpoint
 * (modify_encounter_creature_inc.php) re-fetches + re-sanitizes + re-checks server-side; this is the
 * cosmetic editor. Caller runs the hydration tail (Tom Select / Quill / icons via loadPostFunctions).
 */
export function renderEditEncounterCreatureForm(
    creatureArr: unknown,
    encounterArr: unknown,
    container: HTMLElement,
    _ctx: EncounterCreatureEditCtx = {},
): void {
    const ec = recordOf(recordOf(creatureArr)?.encounter_creatures) ?? {};
    const ecId = ecf(ec, 'encounter_creature_id');
    const name = ecf(ec, 'encounter_creature_name');
    const isPrivate = Number(encounterCreatureField(ec, 'encounter_creature_private')) === 1;
    const isDead = Number(encounterCreatureField(ec, 'encounter_creature_dead')) === 1;
    const gender = ecf(ec, 'gender');
    const monsterTemplateId = ecf(ec, 'encounter_creature_monster_template_id');
    const swarmSize = getSwarmTemplateForCreature(creatureArr as Parameters<typeof getSwarmTemplateForCreature>[0])?.monsterQty ?? 0;

    // Catalog-ref selects: stored value + a display label resolved from the blob.
    const teamId = ecf(ec, 'team');
    // VoiceForge: the GM's half of encounter_creatures.voice_id — a monster/NPC/summon's spoken
    // voice. OPTIONAL by design (blank = silent, the right default for beasts and constructs), and
    // deliberately NOT limited to the player-selectable roster: the premium ancestry voices (Orc,
    // Goblin, Witch…) exist precisely for creatures. ecf() unwraps the display shell the PC compile
    // stamps on this key, so a bare UUID and a shelled ref both read as the id.
    const voiceId = ecf(ec, 'voice_id');
    const summonTypeId = ecf(ec, 'summon_type');
    const controllerId = ecf(ec, 'controller_encounter_creature_id');

    // Media manager: PCs route to the dedicated crop wizard.
    const mediaType = creatureArrItemType(creatureArr) === 'player_character'
        ? 'player_character' : 'encounter_creature';
    const mediaHref = `${BASE_URL}tools/media/media_manager.php?type=${mediaType}&id=${encodeURIComponent(ecId)}`;

    // Carousel — full render_carousel() parity, blob-first (same call render-creature-card.ts makes).
    const presentationPicId = ecf(ec, 'encounter_creature_presentation_pic_id');
    const thumbPicId = ecf(ec, 'encounter_creature_thumbnail_pic_id');
    let canSetPic = false;
    try { canSetPic = hasPlayerPermission(creatureArr); } catch (e) { canSetPic = false; }
    const carouselHtml = buildCarousel({
        domId: domToken(ecId),
        itemName: name,
        pics: pictureListOf(creatureArr),
        videos: itemVideos(creatureArr),
        activePicId: thumbPicId || presentationPicId || null,
        setPic: canSetPic ? { kind: 'creature', encounterCreatureId: ecId, presentationPicId, thumbnailPicId: thumbPicId } : null,
    });

    // Vitality (DB columns on the encounter_creatures bucket — the form edits the base values).
    const vMax = Math.max(0, Number(encounterCreatureField(ec, 'v_max') ?? 0) || 0);
    const vCurrentRaw = Number(encounterCreatureField(ec, 'v_current') ?? 0) || 0;
    const vCurrent = Math.min(Math.max(0, vCurrentRaw), vMax);
    const vTemp = Number(encounterCreatureField(ec, 'v_temp') ?? 0) || 0;
    const vMaxTemp = Number(encounterCreatureField(ec, 'v_max_temp') ?? 0) || 0;

    // Position + bounds (bounds live in the encounter's computed cluster).
    const xPos = posCoord(creatureArr, 'x_pos') ?? 0;
    const zPos = posCoord(creatureArr, 'z_pos') ?? 0;
    const rangeMinX = Number(computedValue(encounterArr, 'encounter', 'encounter_range_min_x') ?? -100);
    const rangeMaxX = Number(computedValue(encounterArr, 'encounter', 'encounter_range_max_x') ?? 100);
    const rangeMinZ = Number(computedValue(encounterArr, 'encounter', 'encounter_range_min_z') ?? -100);
    const rangeMaxZ = Number(computedValue(encounterArr, 'encounter', 'encounter_range_max_z') ?? 1000);

    // Merchant favor — only when the creature is a merchant (PHP gated on layer_value(...,'merchant')).
    const isMerchant = !!layerOf(creatureArr, 'merchant');
    const merchantFavorRaw = encounterCreatureField(ec, 'merchant_favor');
    const merchantFavor = (merchantFavorRaw === undefined || merchantFavorRaw === null || merchantFavorRaw === '')
        ? 50 : Number(merchantFavorRaw);
    const merchantSection = isMerchant
        ? `<div class="p-2 merchant-favor-modify-section">
            ${slider('merchant_favor', 'Merchant favor', 'merchant', 0, 100, 1, merchantFavor)}
            <div class="small text-muted mt-2 border rounded p-2 merchant-favor-gm-preview"></div>
        </div>`
        : '';

    const TEXTAREAS = [
        'encounter_creature_flavor_text', 'encounter_creature_personality', 'encounter_creature_quirks',
        'encounter_creature_motivations', 'encounter_creature_secrets', 'encounter_creature_tactics',
        'encounter_creature_gm_notes',
    ];

    // ── SAVE BASELINE (2026-07-15 — controlled-summon revert bug) ───────────────────────────────────
    // This form paints from the CACHED creature node (findCached() above is cache-first), and
    // modifyEncounterCreature() re-POSTs EVERY field on EVERY single change. So a cache lagging a
    // recent edit silently rewrites the stale value back over it: the owner's summoned ghoul lost the
    // controller + friendly team it had been given minutes earlier, having already shown up correctly
    // in the player's summons tab. Ship the exact values we PAINTED; modify_encounter_creature_inc.php
    // then writes a column only when the submitted value DIFFERS from its baseline (i.e. the user
    // actually touched that field), so an untouched field can never clobber a newer value — no matter
    // how stale this form is. Absent (old cached bundle) → the endpoint falls back to last-write-wins.
    //
    // Quill/HTML fields are deliberately EXCLUDED: Quill re-normalizes markup when it mounts, so
    // painted !== read-back even for an untouched field and the compare could not be trusted. They
    // keep the existing last-write-wins behavior.
    const saveBaseline: Record<string, string> = {
        encounter_creature_name: name,
        encounter_creature_monster_template_id: monsterTemplateId,
        gender,
        team: teamId,
        voice_id: voiceId,
        controller_encounter_creature_id: controllerId,
        summon_type: summonTypeId,
        creature_number: ecf(ec, 'creature_number'),
        creature_initiative: ecf(ec, 'creature_initiative'),
        v_current: String(vCurrent),
        v_max: String(vMax),
        v_temp: String(vTemp),
        v_max_temp: String(vMaxTemp),
        mana: ecf(ec, 'mana'),
        spell_list_prepared: ecf(ec, 'spell_list_prepared'),
        aps: ecf(ec, 'aps'),
        aps_total: ecf(ec, 'aps_total'),
        x_pos: String(xPos),
        z_pos: String(zPos),
        encounter_creature_dead: isDead ? '1' : '0',
        // No encounter_creature_private entry: this form paints no control for it (privacy is the
        // privateButtons() toggle -> update_privacy_inc.php), modifyEncounterCreature() no longer sends
        // it, and the endpoint reads it from the row regardless. Nothing to baseline.
    };

    const html = `
<div class="row gx-0 text-left encounter-creature-container-form">

    <div class="col-12 p-2"><h4>${icon('creature')} Modify Creature</h4></div>

    <div class="d-flex align-items-center justify-content-center">
        ${carouselHtml}
    </div>

    <input id="input_hidden_encounter_creature_id" type="hidden" value="${esc(ecId)}">
    <input id="input_hidden_mec_save_baseline" type="hidden" value="${esc(JSON.stringify(saveBaseline))}">

    <div class="d-flex align-items-center p-2 flex-wrap">
        <div class="pr-2 pb-1">
            <a href="${esc(mediaHref)}" id="btn_media_manager_encounter_creature_${esc(ecId)}" role="button"
                class="btn btn-sm btn-info" title="Media Manager - picture gallery + upload">${icon('modify_encounter_creature_pictures')}</a>
        </div>
        <div class="pr-2 pb-1">${hideButtons(ecId)}</div>
        <div class="pb-1">${privateButtons(ecId, isPrivate)}</div>
    </div>

    <div class="p-2 d-flex align-items-center gap-2">
        <span class="flex-shrink-0 d-inline-flex align-items-center">${icon('character_name', 25, 25, true)}</span>
        <div class="flex-grow-1">
            <input type="text" class="form-control" id="encounter_creature_name" name="encounter_creature_name"
                placeholder="Encounter Creature Name" value="${esc(name)}" data-ft-mec-change="modify">
        </div>
        <div class="pl-2">
            <button class="btn btn-primary" ${mecAttrs('randomize-name')}>${icon('random')}</button>
        </div>
    </div>

    ${creatureArrItemType(creatureArr) === 'creature' ? `<div class="p-2">
        <label class="form-label" for="encounter_creature_swarm_size">Swarm size</label>
        <select class="form-select" id="encounter_creature_swarm_size" data-ft-mec-change="modify" data-saved-value="${swarmSize}">
            ${[0, ...Object.values(SWARM_TEMPLATES).map(def => def.monsterQty)].map(size =>
                `<option value="${size}"${size === swarmSize ? ' selected' : ''}>${size || 'None'}</option>`).join('')}
        </select>
    </div>` : ''}

    <div class="p-2">
        <div class="d-flex align-items-center justify-content-start ft-dynamic-text-muted col-12">
            <div class="pl-1 pr-2">${icon('monster_template', 25, 25, true)}</div>
            <div class="form-floating w-100">
                <input type="text" class="form-control" id="encounter_creature_monster_template_id"
                    name="encounter_creature_monster_template_id" placeholder="&#160;" value="${esc(monsterTemplateId)}"
                    data-ft-mec-change="modify">
                <label for="encounter_creature_monster_template_id">Monster Template Id</label>
            </div>
        </div>
        <div class="form-text" data-ft-mec-template-details>${monsterTemplateId ? '' : 'No monster template'}</div>
    </div>

    ${genderGroup(gender)}

    ${TEXTAREAS.map((id) => `<div class="px-2">${quillTextarea(id, ecf(ec, id))}</div>`).join('')}

    <div class="px-2">${dynamicSelect({ id: 'team', itemType: 'team_type', preselectValue: teamId, preselectLabel: selectLabel(creatureArr, ec, 'team', teamId), iconKey: 'team' })}</div>

    <div class="px-2">${dynamicSelect({ id: 'voice_id', itemType: 'voice', preselectValue: voiceId, preselectLabel: selectLabel(creatureArr, ec, 'voice_id', voiceId), iconKey: 'voice' })}</div>

    ${merchantSection}

    <div class="p-2">
        <div class="d-flex align-items-center w-100 min-w-0 gap-2">
            <div class="pl-2 flex-shrink-0 d-flex align-items-center">${icon('controller_role', 25, 25, true, 'Controller')}</div>
            <div class="flex-grow-1 min-w-0">
                <select class="ts-select" id="controller_encounter_creature_id" title="Select Controller" data-ft-mec-change="modify">
                    ${controllerOptions(encounterArr, controllerId)}
                </select>
            </div>
        </div>
    </div>

    <div class="p-2">${dynamicSelect({ id: 'summon_type', itemType: 'summon_type', preselectValue: summonTypeId, preselectLabel: selectLabel(creatureArr, ec, 'summon_type', summonTypeId), iconKey: 'summon_type' })}</div>

    <div class="p-2 d-flex flex-wrap gap-3 align-items-end">
        <div style="max-width: 9rem;">${numberField('creature_number', ecf(ec, 'creature_number'), 'creature_number', 0, 100000, 1)}</div>
        <div style="max-width: 9rem;">${numberField('creature_initiative', ecf(ec, 'creature_initiative'), 'initiative', 0, 100000, 1)}</div>
    </div>

    <div class="p-2 ec-v-slider-wrap">
        ${slider('v_current', '', 'v_current', 0, vMax, 1, vCurrent, 'ec_modify_v_slider_max_label')}
    </div>

    <div class="p-2 d-flex gap-2">
        <div class="col-4">${numberField('v_max', String(vMax), 'v_max', 0, 50000, 1)}</div>
        <div class="col-4">${numberField('v_temp', String(vTemp), 'v_temp', 0, 50000, 1)}</div>
        <div class="col-4">${numberField('v_max_temp', String(vMaxTemp), 'v_max_temp', -50000, 50000, 1)}</div>
    </div>

    <div class="p-2">${checkbox('encounter_creature_dead', 'dead', '', isDead)}</div>

    <div class="p-2 d-flex flex-wrap gap-3 align-items-end">
        <div style="max-width: 9rem;">${numberField('mana', ecf(ec, 'mana'), 'mana', 0, 10000, 1)}</div>
        <div style="max-width: 6rem;">${numberField('spell_list_prepared', ecf(ec, 'spell_list_prepared'), 'prepared_spells', 0, 3, 1, 'Prepared Spell List')}</div>
    </div>

    <div class="p-2 d-flex flex-wrap gap-4 align-items-end">
        <div style="max-width: 7rem;">${numberField('aps', ecf(ec, 'aps'), 'aps', 0, 20, 1)}</div>
        <div style="max-width: 7rem;">${numberField('aps_total', ecf(ec, 'aps_total'), 'aps_total', 0, 20, 1)}</div>
    </div>

    <div class="d-flex align-items-center justify-content-center mb-2">
        ${slider('x_pos', 'X Position', 'x_pos', rangeMinX, rangeMaxX, 1, xPos)}
    </div>

    <div class="d-flex align-items-center justify-content-center mb-2">
        ${slider('z_pos', 'Z Position', 'z_pos', rangeMinZ, rangeMaxZ, 1, zPos)}
    </div>

    <div class="mt-3 col-12" id="div_refresh_side_form"></div>
</div>`;

    container.innerHTML = html;

    if (monsterTemplateId) {
        const details = container.querySelector<HTMLElement>('[data-ft-mec-template-details]');
        const itemId = ecf(ec, 'item_id');
        void withAsyncDeadline(signal => monsterTemplateSelectionInputs(itemId, monsterTemplateId, signal), 20_000)
            .then(inputs => {
                if (details?.isConnected && container.querySelector<HTMLInputElement>('#encounter_creature_monster_template_id')?.value === monsterTemplateId)
                    details.textContent = selectedTemplateDetails(inputs, itemId, monsterTemplateId).label;
            }).catch(() => { if (details?.isConnected) details.textContent = 'Template details unavailable.'; });
    }

    // The vitality slider/max coupling is normally wired by an inline <script> in the PHP form, but
    // scripts injected via innerHTML don't execute — bind it programmatically (no-op if unavailable).
    try {
        modifyEncounterHost().ftBindVitalitySliderToVMax?.('v_max', 'v_current', 'ec_modify_v_slider_max_label');
    } catch (e) { /* non-fatal */ }
}

/**
 * LAIR edit form — a lair is an item-like encounter creature with no stats/team/gender/vitality/
 * position, so the full Modify Creature form (25 fields) is mostly N/A. This focused form edits only
 * the lair-relevant fields: picture (media manager + carousel), hide/private toggles, name, lair_dc
 * (the single manual save DC), flavor text, and GM notes. Fields save through modifyLair(event) →
 * modify_lair_inc.php (a targeted UPDATE that never touches team/gender/vitality). Quill + icon
 * hydration is the same loadPostFunctions() tail the caller runs.
 */
export function renderEditLairForm(
    creatureArr: unknown,
    _encounterArr: unknown,
    container: HTMLElement,
    _ctx: EncounterCreatureEditCtx = {},
): void {
    const ec = recordOf(recordOf(creatureArr)?.encounter_creatures) ?? {};
    const ecId = ecf(ec, 'encounter_creature_id');
    const name = ecf(ec, 'encounter_creature_name');
    const lairDc = ecf(ec, 'lair_dc');
    // Lairs default to the Enemy team; the rare friendly/neutral lair is set here (2026-07-10).
    const teamId = ecf(ec, 'team');
    const isPrivate = Number(encounterCreatureField(ec, 'encounter_creature_private')) === 1;

    const mediaHref = `${BASE_URL}tools/media/media_manager.php?type=encounter_creature&id=${encodeURIComponent(ecId)}`;
    const presentationPicId = ecf(ec, 'encounter_creature_presentation_pic_id');
    const thumbPicId = ecf(ec, 'encounter_creature_thumbnail_pic_id');
    let canSetPic = false;
    try { canSetPic = hasPlayerPermission(creatureArr); } catch (e) { canSetPic = false; }
    const carouselHtml = buildCarousel({
        domId: domToken(ecId),
        itemName: name,
        pics: pictureListOf(creatureArr),
        videos: itemVideos(creatureArr),
        activePicId: thumbPicId || presentationPicId || null,
        setPic: canSetPic ? { kind: 'creature', encounterCreatureId: ecId, presentationPicId, thumbnailPicId: thumbPicId } : null,
    });

    // Quill field wired to modifyLair (quillTextarea() hardcodes modifyEncounterCreature, so inline it).
    const lairQuill = (base: string, value: string): string => {
        const v = esc(value);
        const shortKey = base.replace(/^encounter_creature_/, '');
        return `
        <div class="d-flex flex-column align-items-start justify-content-start mb-4">
            <div class="d-flex align-items-center w-100 mb-2">
                <div class="pe-2">${icon(shortKey)}</div>
                <div class="flex-grow-1"><span class="fw-bold text-decoration-underline">${esc(labelize(shortKey))}:</span></div>
            </div>
            <div class="w-100 quill-editor-container">
                <div class="quill-editor" id="quill_${esc(base)}" data-layer-type-name="${esc(base)}"
                    data-initial-content="${v}" data-update-callback="modifyLair(event)"></div>
                <textarea id="${esc(base)}" name="${esc(base)}" style="display: none;">${v}</textarea>
            </div>
        </div>`;
    };

    container.innerHTML = `
<div class="row gx-0 text-left lair-container-form">

    <div class="col-12 p-2"><h4>${icon('lair')} Modify Lair</h4></div>

    <div class="d-flex align-items-center justify-content-center">
        ${carouselHtml}
    </div>

    <input id="input_hidden_encounter_creature_id" type="hidden" value="${esc(ecId)}">

    <div class="d-flex align-items-center p-2 flex-wrap">
        <div class="pr-2 pb-1">
            <a href="${esc(mediaHref)}" role="button" class="btn btn-sm btn-info"
                title="Media Manager - picture gallery + upload">${icon('modify_encounter_creature_pictures')}</a>
        </div>
        <div class="pr-2 pb-1">${hideButtons(ecId)}</div>
        <div class="pb-1">${privateButtons(ecId, isPrivate)}</div>
    </div>

    <div class="p-2 d-flex align-items-center gap-2">
        <span class="flex-shrink-0 d-inline-flex align-items-center">${icon('character_name', 25, 25, true)}</span>
        <div class="flex-grow-1">
            <input type="text" class="form-control" id="encounter_creature_name" name="encounter_creature_name"
                placeholder="Lair Name" value="${esc(name)}" data-ft-mec-change="modify-lair">
        </div>
    </div>

    <div class="p-2 d-flex align-items-center gap-2">
        <span class="flex-shrink-0 d-inline-flex align-items-center">${icon('lair_ability', 25, 25, true, 'Lair Save DC')}</span>
        <div class="form-floating flex-grow-1">
            <input type="number" class="form-control form-control-sm" id="lair_dc" name="lair_dc" min="0" max="255" step="1"
                placeholder="&#160;" value="${esc(lairDc)}" data-ft-mec-change="modify-lair">
            <label for="lair_dc">Lair Save DC (blank = each ability's own DC)</label>
        </div>
    </div>

    <div class="px-2 mb-1">${dynamicSelect({ id: 'team', itemType: 'team_type', preselectValue: teamId, preselectLabel: selectLabel(creatureArr, ec, 'team', teamId), iconKey: 'team', changeAction: 'modify-lair' })}</div>

    <div class="px-2">${lairQuill('encounter_creature_flavor_text', ecf(ec, 'encounter_creature_flavor_text'))}</div>
    <div class="px-2">${lairQuill('encounter_creature_gm_notes', ecf(ec, 'encounter_creature_gm_notes'))}</div>

    <div class="mt-3 col-12" id="div_refresh_side_form"></div>
</div>`;
}

/**
 * Orchestrator: open the Modify Creature side form for `ecId`, painted entirely from cache (no DB form
 * fetch). Mirrors openEditInventoryForm — cache-first resolve with a refetch-and-retry on miss, then
 * loadPostFunctions() runs the hydration tail (Tom Select / Quill / icons) the old queryData would have.
 */
export async function openEditEncounterCreatureForm(ecId: string): Promise<void> {
    const w = modifyEncounterHost();

    // Bring the log + Forms tab to front BEFORE injecting HTML + loadPostFunctions (matches openSideForm).
    try { forceOpenSideLog(); } catch (e) { /* */ }
    try { (document.getElementById('pills_sidelog_form_tab') as HTMLElement | null)?.click(); } catch (e) { /* */ }

    const container = document.getElementById('div_refresh_sidelog_form');
    if (!container) return;

    // An encounter creature is keyed in casterCache by its encounter_creature_id (the same blob the
    // side-info card / sheet panes render from) — NOT by items.item_id, so ftFindFullNestedItem (which
    // matches the template id) misses. Resolve like the panes do: casterCache.get → encounter-seed →
    // fetchAndCacheCaster (which itself seeds-from-encounter or does the server compile), then re-get.
    const getFromCasterCache = (): CreatureRecord | null => {
        try {
            const c = w.FateTide?.casterCache;
            if (!c || typeof c.get !== 'function') return null;
            return recordOf(c.get(ecId) || c.get(ecId.toUpperCase()) || c.get(ecId.toLowerCase()));
        } catch (e) { return null; }
    };
    const findCached = (): CreatureRecord | null => {
        const hit = getFromCasterCache();
        if (hit) return hit;
        try {
            if (typeof w.ftSeedCasterFromEncounterCache === 'function' && w.ftSeedCasterFromEncounterCache(ecId)) {
                const seeded = getFromCasterCache();
                if (seeded) return seeded;
            }
        } catch (e) { /* */ }
        return null;
    };

    let creatureArr = findCached();
    if (!creatureArr && typeof w.fetchAndCacheCaster === 'function') {
        try { await w.fetchAndCacheCaster(ecId); creatureArr = findCached(); } catch (e) { /* */ }
    }

    if (!creatureArr) {
        const warn = document.getElementById('div_sidelog_warning');
        if (warn) {
            warn.innerHTML = '<h5 class="bg-warning text-dark rounded border border-2 border-danger p-3 my-2">'
                + "Couldn't load that creature to edit. Try reopening it." + '</h5>';
        }
        try { forceOpenSideLog(); } catch (e) { /* */ }
        return;
    }

    // Encounter context for the controller roster (and X/Z bounds): cache-first, then DB fallback.
    const encId = String(encounterCreatureField(creatureArr.encounter_creatures, 'encounter_id') ?? '');
    let encounterArr: unknown = null;
    try {
        const cache = w.FateTide?.encounterCache;
        if (cache && encId && typeof cache.get === 'function') {
            encounterArr = cache.get(encId) || cache.get(encId.toUpperCase()) || cache.get(encId.toLowerCase()) || null;
        }
    } catch (e) { /* */ }
    if (!Array.isArray(recordOf(encounterArr)?.encounter_creatures_arr) && encId && typeof w.getEncounterArr === 'function') {
        try { encounterArr = await w.getEncounterArr(encId); } catch (e) { /* */ }
    }

    // Lairs get the focused Modify Lair form (no team/gender/vitality/stat fields); everything else
    // uses the full Modify Creature form.
    const isLair = creatureArrItemType(creatureArr).toLowerCase() === 'lair';
    if (isLair) {
        renderEditLairForm(creatureArr, encounterArr, container, { encounter_id: encId });
    } else {
        renderEditEncounterCreatureForm(creatureArr, encounterArr, container, { encounter_id: encId });
    }

    // Hydrate the freshly-painted form (Tom Select catalog load, Quill editors, icon mounts) — the same
    // tail queryData would have run.
    await loadPostFunctions();
}

/**
 * Save handler for the Modify Lair form. Syncs the two Quill editors, reads the lair-relevant fields,
 * and POSTs them to modify_lair_inc.php (a targeted UPDATE — no team/gender/vitality). Mirrors
 * modifyEncounterCreature's refresh tail (side-form message, roster rebuild, hydration).
 */
async function modifyLair(event: Event): Promise<void> {
    const container = event.target instanceof Element ? event.target.closest<HTMLElement>('.lair-container-form') : null;
    if (!container) { console.error('modifyLair: no .lair-container-form found'); return; }

    // Sync Quill HTML into the hidden textareas (same as modifyEncounterCreature).
    const Q = modifyEncounterHost().Quill;
    ['encounter_creature_flavor_text', 'encounter_creature_gm_notes'].forEach((id) => {
        const editor = container.querySelector(`.quill-editor[data-layer-type-name="${id}"]`);
        const textarea = container.querySelector(`#${id}`) as HTMLTextAreaElement | null;
        if (editor && textarea && Q && typeof Q.find === 'function') {
            const quill = Q.find(editor);
            if (quill) textarea.value = quill.root.innerHTML;
        }
    });

    const val = (sel: string): string => (container.querySelector(sel) as HTMLInputElement | null)?.value ?? '';
    const payload = {
        encounter_creature_id: val('#input_hidden_encounter_creature_id'),
        encounter_creature_name: val('#encounter_creature_name'),
        lair_dc: val('#lair_dc'),
        team: val('#team'),
        encounter_creature_flavor_text: val('#encounter_creature_flavor_text'),
        encounter_creature_gm_notes: val('#encounter_creature_gm_notes'),
    };

    try {
        const response = await cardEditFetch(`${BASE_URL}options/tools/encounter_creatures/modify_lair_inc.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const result = await response.text();
        const sideForm = document.getElementById('div_refresh_side_form');
        if (sideForm) sideForm.innerHTML = result;
        const w = modifyEncounterHost();
        await loadPostFunctions();
    } catch (error) {
        console.error('Error in modifyLair:', error);
    }
}

Object.assign(window, {
    renderEditEncounterCreatureForm,
    renderEditLairForm,
    openEditEncounterCreatureForm,
    modifyLair,
});
